import React, { useState, useEffect } from 'react';
import { Truck, RefreshCw, Printer, Check, Copy, Trash2, Clock, MapPin, Info, Package } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../../../service/api';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';

const COLOR_CONFIG: Record<string, { text: string; bg: string }> = {
  YURTİÇİ: { text: "text-orange-600 dark:text-orange-400", bg: "bg-orange-500", },
  ARAS: { text: "text-red-600 dark:text-red-400", bg: "bg-red-500" },
  MNG: { text: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500" },
  PTT: { text: "text-amber-600 dark:text-amber-400", bg: "bg-amber-500" },
  "KOLAY GELSİN": { text: "text-purple-600 dark:text-purple-400", bg: "bg-purple-500", },
  "DEPO TESLİM": { text: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500", },
  "GEL-AL": { text: "text-surface-600 dark:text-surface-400", bg: "bg-surface-400", },
  "GEL AL": { text: "text-surface-600 dark:text-surface-400", bg: "bg-surface-400", },
  "-": { text: "text-surface-600 dark:text-surface-400", bg: "bg-surface-400" },
};

interface CargoStatusWidgetProps {
  order: any;
  onUpdate: () => void;
}

export const CargoStatusWidget: React.FC<CargoStatusWidgetProps> = ({ order, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [showZplModal, setShowZplModal] = useState(false);
  const [showEventsModal, setShowEventsModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cargoCount, setCargoCount] = useState(1);
  const [events, setEvents] = useState<any[]>([]);

  const fetchEvents = async () => {
    if (!order?.id) return;
    try {
      const res = await api.get(`/cargo/order/${order.id}/events`);
      setEvents(res.data || []);
    } catch (error) {
      console.error("Kargo hareketleri alınamadı", error);
    }
  };

  const handleCreateCargo = async () => {
    try {
      setLoading(true);
      await api.post(`/cargo/order/${order.id}/create`, { cargoCount });
      toast.success('Kargo başarıyla oluşturuldu.');
      onUpdate();
      fetchEvents();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Kargo oluşturulurken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setLoading(true);
      await api.get(`/cargo/order/${order.id}/status`);
      toast.success('Kargo durumu ve etiket başarıyla güncellendi.');
      onUpdate();
      fetchEvents();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Kargo durumu güncellenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCargo = async () => {
    if (!window.confirm("Bu kargoyu iptal etmek istediğinize emin misiniz? Bu işlem kargo firmasından da gönderiyi iptal edecektir.")) {
      return;
    }

    try {
      setLoading(true);
      await api.post(`/cargo/order/${order.id}/cancel`);
      toast.success('Kargo başarıyla iptal edildi.');
      onUpdate();
      setEvents([]);
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Kargo iptal edilirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyZpl = () => {
    if (order.cargoBarcodeData) {
      navigator.clipboard.writeText(order.cargoBarcodeData);
      setCopied(true);
      toast.success('ZPL Kodu Kopyalandı');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isRealTrackingNo = Boolean(
    order?.cargoTrackingNo &&
    String(order.cargoTrackingNo).trim() !== String(order.orderNumber || '').trim() &&
    String(order.cargoTrackingNo).trim() !== String(order.cargoIntegrationNo || '').trim() &&
    String(order.cargoTrackingNo).trim() !== String(order.id || '').trim()
  );

  useEffect(() => {
    // Gerçek takip no henüz oluşmamışsa otomatik sorgula
    if (order && order.cargoTrackingNo && !isRealTrackingNo) {
      handleUpdateStatus();
    } else if (isRealTrackingNo) {
      fetchEvents();
    }
  }, [order?.cargoTrackingNo, isRealTrackingNo]);

  if (!order) return null;

  const isIntegratedCargo = () => {
    if (!order.shipment) return false;
    const shipment = order.shipment.toUpperCase();
    return shipment.includes('YURT') || shipment.includes('ARAS');
  };

  if (!isIntegratedCargo()) return null;

  const hasTrackingNo = !!order.cargoTrackingNo;
  const hasZpl = !!order.cargoBarcodeData;
  const latestEvent = events.length > 0 ? events[0] : null;

  return (
    <div className="card2 p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between border-b border-borderColor pb-2">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest opacity-60">
          <Truck size={14} /> Kargo Entegrasyonu
        </div>
        {events.length > 0 && (
          <button
            onClick={() => setShowEventsModal(true)}
            className="text-[10px] font-bold text-primary flex items-center gap-1 hover:underline"
          >
            <Info size={12} /> Tüm Hareketler
          </button>
        )}
      </div>

      {!hasTrackingNo ? (
        <div className="flex items-center justify-between p-3 border border-dashed border-borderColor rounded-xl bg-bgAlt/50 gap-3">
          <div className="flex flex-col gap-1.5">
             <div className="flex items-center gap-2">
                 <Truck size={14} className="text-text/40" />
                 <span className="text-[10px] font-black opacity-40 uppercase tracking-widest text-text">Firma</span>
             </div>
             {(() => {
               const shipmentText = (order.shipment || order.cargoProvider || "-").trim().toUpperCase();
               const config = COLOR_CONFIG[shipmentText] || { text: "text-text", bg: "bg-borderColor" };
               return (
                 <div className="flex items-center gap-1.5">
                   <div className={`w-2 h-2 rounded-full ${config.bg} animate-pulse`} />
                   <span className={`text-[11px] font-black tracking-widest uppercase ${config.text}`}>
                     {shipmentText}
                   </span>
                 </div>
               );
             })()}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center bg-bg border border-borderColor rounded-lg px-2 h-8">
              <Package size={12} className="text-text/40 mr-1.5" />
              <input
                type="number"
                min={1}
                value={cargoCount}
                onChange={(e) => setCargoCount(Number(e.target.value) || 1)}
                className="w-8 bg-transparent text-xs font-bold text-center outline-none text-text"
              />
            </div>

            <button
              onClick={handleCreateCargo}
              disabled={loading}
              className="px-3 h-8 bg-primary text-white text-[10px] font-bold uppercase tracking-widest rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-1.5 disabled:opacity-50 shrink-0"
            >
              {loading ? <RefreshCw size={12} className="animate-spin" /> : <Truck size={12} />}
              {loading ? '...' : 'Oluştur'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 p-3 bg-bgAlt rounded-xl border border-borderColor">
            <div className="flex flex-col gap-1">
              <span className="text-[9px] font-black opacity-40 uppercase">Kargo Firması</span>
              <span className="text-xs font-bold text-primary">{order.shipment || order.cargoProvider || '-'}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[9px] font-black opacity-40 uppercase">Takip Numarası</span>
              {isRealTrackingNo ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-xs font-bold text-primary break-all">{order.cargoTrackingNo}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(String(order.cargoTrackingNo));
                      toast.success(`Kargo takip no kopyalandı: ${order.cargoTrackingNo}`);
                    }}
                    className="p-1 text-text/40 hover:text-primary hover:bg-primary/10 rounded transition-colors"
                    title="Takip Numarasını Kopyala"
                  >
                    <Copy size={12} />
                  </button>
                </div>
              ) : (
                <span className="text-xs font-medium text-orange-500">
                  Kargoda Bekliyor (Henüz Takip No Oluşmadı)
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2 col-span-2 pt-2 border-t border-borderColor/50">
              <span className="text-[9px] font-black opacity-40 uppercase">Güncel Durum</span>
              {latestEvent ? (
                <div className="flex flex-col gap-1 p-2 bg-bg rounded-lg border border-borderColor/50 text-[11px]">
                  <div className="flex justify-between items-start font-bold text-primary">
                    <span>{latestEvent.eventName || latestEvent.status}</span>
                    {latestEvent.eventDate && (
                      <span className="text-[10px] opacity-70 whitespace-nowrap ml-2 text-text">
                        {new Date(latestEvent.eventDate).toLocaleDateString('tr-TR')} {latestEvent.eventTime || new Date(latestEvent.eventDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  {(latestEvent.unitName || latestEvent.cityName) && (
                    <div className="flex items-center gap-1 text-[10px] opacity-70">
                      <MapPin size={10} />
                      {latestEvent.unitName} {latestEvent.townName} {latestEvent.cityName ? `(${latestEvent.cityName})` : ''}
                    </div>
                  )}
                  {latestEvent.reasonName && (
                    <div className="text-[10px] text-red-500 font-medium">
                      Sebep: {latestEvent.reasonName}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-xs font-bold">{order.cargoStatus || 'Bilinmiyor'}</span>
              )}
            </div>
          </div>

          <div className="flex gap-2 mt-2 flex-wrap">
            <button
              onClick={handleUpdateStatus}
              disabled={loading}
              className="flex-1 min-w-[140px] px-3 py-2 border border-primary text-primary hover:bg-primary/5 text-xs font-bold rounded-lg transition-colors flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Durumu Güncelle
            </button>

            {hasZpl && (
              <button
                onClick={() => setShowZplModal(true)}
                disabled={loading}
                className="flex-1 min-w-[140px] px-3 py-2 bg-text text-bg hover:opacity-90 text-xs font-bold rounded-lg transition-colors flex justify-center items-center gap-2 disabled:opacity-50"
              >
                <Printer size={14} />
                Etiketi Yazdır
              </button>
            )}
            {/*
            <button 
              onClick={handleCancelCargo}
              disabled={loading}
              className="flex-1 min-w-[140px] px-3 py-2 border border-red-500 text-red-500 hover:bg-red-500/10 text-xs font-bold rounded-lg transition-colors flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Trash2 size={14} />
              İptal Et
            </button>
             */}
          </div>
        </div>
      )}

      {/* Events Modal */}
      <Dialog
        header="Kargo Hareketleri"
        visible={showEventsModal}
        style={{ width: '40vw' }}
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        onHide={() => setShowEventsModal(false)}
      >
        <div className="flex flex-col gap-4">
          {events.length === 0 ? (
            <div className="text-center p-4 opacity-50 text-xs">Hareket bulunamadı.</div>
          ) : (
            <div className="relative border-l-2 border-primary/20 ml-3 pl-4 space-y-4 py-2">
              {events.map((ev, idx) => (
                <div key={ev.id || idx} className="relative">
                  {/* Timeline dot */}
                  <div className="absolute -left-[23px] top-1 w-4 h-4 rounded-full bg-bg border-2 border-primary flex items-center justify-center">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  </div>

                  <div className="flex flex-col gap-1 p-3 bg-bgAlt rounded-xl border border-borderColor text-xs">
                    <div className="flex justify-between items-start font-bold text-primary">
                      <span>{ev.eventName || ev.status}</span>
                      {ev.eventDate && (
                        <span className="text-[10px] opacity-70 whitespace-nowrap ml-2 text-text">
                          {new Date(ev.eventDate).toLocaleDateString('tr-TR')} {ev.eventTime || new Date(ev.eventDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>

                    {(ev.unitName || ev.cityName) && (
                      <div className="flex items-center gap-1 text-[10px] opacity-70 mt-1">
                        <MapPin size={12} />
                        {ev.unitName} {ev.townName} {ev.cityName ? `(${ev.cityName})` : ''}
                      </div>
                    )}

                    {ev.reasonName && (
                      <div className="text-[10px] text-red-500 font-medium mt-1 bg-red-500/10 p-1.5 rounded-md inline-block">
                        Sebep: {ev.reasonName}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Dialog>

      {/* ZPL Modal */}
      <Dialog
        header="Kargo Etiketi (ZPL)"
        visible={showZplModal}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '640px': '95vw' }}
        onHide={() => setShowZplModal(false)}
      >
        <div className="flex flex-col gap-4">
          <p className="text-xs opacity-70">
            Aşağıdaki ZPL kodunu kopyalayarak barkod yazıcınız üzerinden (örn: Zebra) doğrudan yazdırabilirsiniz.
          </p>
          <div className="relative">
            <InputTextarea
              value={order.cargoBarcodeData || ''}
              rows={15}
              className="w-full text-xs font-mono p-3 bg-bgAlt border-borderColor rounded-xl"
              readOnly
            />
            <button
              onClick={handleCopyZpl}
              className="absolute top-2 right-2 p-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-md flex items-center gap-1 text-[10px] font-bold"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Kopyalandı' : 'Kopyala'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

